import { parseResults, parseToolCalls } from "./utils";
import type { ActionResult, ParsedToolCall, RunFrame, StepRecord } from "@/types/fleet";

/**
 * One shape for a step, whichever end it arrived from.
 *
 * A historical step is a `StepRecord` off `GET /api/v1/runs/{id}/steps` — full
 * observation text, tool calls, results. A live step is a `run.step` event
 * (fleet-v1.md §6), which carries a pre-rendered `line` and the tool *names*
 * but not the observation, because events are ephemeral and the device is not
 * going to push 40 lines of screen dump over the console socket per step.
 *
 * The timeline renders both, so both normalise to this and the card never has
 * to ask which one it is holding — only whether a field is present.
 */
export interface TimelineStep {
  n: number;
  thinking: boolean;
  latencyMs: number | null;
  ttftMs: number | null;
  tokens: { prompt: number; completion: number; reasoning: number } | null;
  createdAt: number | null;
  observationText: string | null;
  observationHash: string | null;
  reasoningText: string | null;
  toolCalls: ParsedToolCall[];
  results: ActionResult[];
  screenshotPath: string | null;
  /** The device's own one-line summary; the only detail a live frame has. */
  line: string | null;
  /** True while this step exists only as an event, with no DB row behind it. */
  live: boolean;
}

export function fromStepRecord(s: StepRecord): TimelineStep {
  return {
    n: s.n,
    thinking: s.thinking,
    latencyMs: s.latencyMs ?? null,
    ttftMs: s.ttftMs || null,
    tokens: {
      prompt: s.promptTokens ?? 0,
      completion: s.completionTokens ?? 0,
      reasoning: s.reasoningTokens ?? 0,
    },
    createdAt: s.createdAt ?? null,
    observationText: s.observationText ?? null,
    observationHash: s.observationHash ?? null,
    reasoningText: s.reasoningText ?? null,
    toolCalls: parseToolCalls(s.toolCallsJson),
    results: parseResults(s.resultsJson),
    screenshotPath: s.screenshotPath ?? null,
    line: null,
    live: false,
  };
}

export function fromRunStepFrame(f: Extract<RunFrame, { kind: "run.step" }>): TimelineStep {
  return {
    n: f.n,
    thinking: f.thinking ?? false,
    latencyMs: f.latency_ms ?? null,
    ttftMs: null,
    tokens: f.tokens
      ? { prompt: f.tokens.prompt, completion: f.tokens.completion, reasoning: f.tokens.reasoning }
      : null,
    createdAt: Date.now(),
    observationText: null,
    observationHash: f.observation_hash ?? null,
    reasoningText: null,
    // A live frame names the tools but not their arguments, so each becomes a
    // bare call the card renders by name alone.
    toolCalls: (f.tools ?? []).map((t) => ({ tool: t, args: {} })),
    results: [],
    screenshotPath: null,
    line: f.line ?? null,
    live: true,
  };
}

/**
 * Merge the persisted steps with whatever the socket has pushed since.
 *
 * A DB row always wins over a live frame for the same `n`: it is the same step,
 * only complete. This is what makes the timeline settle rather than flicker as
 * the backfill lands behind the tail.
 */
export function mergeSteps(persisted: TimelineStep[], live: Map<number, TimelineStep>): TimelineStep[] {
  const byN = new Map<number, TimelineStep>();
  for (const s of live.values()) byN.set(s.n, s);
  for (const s of persisted) byN.set(s.n, s);
  return [...byN.values()].sort((a, b) => a.n - b.n);
}

/** Did any tool call in this step fail? Failures expand by default. */
export function stepFailed(s: TimelineStep): boolean {
  return s.results.some((r) => !r.ok);
}

export function stepTotalTokens(s: TimelineStep): number {
  if (!s.tokens) return 0;
  return s.tokens.prompt + s.tokens.completion + s.tokens.reasoning;
}

/** Wall-clock gap between this step and the previous one. */
export function stepElapsedMs(s: TimelineStep, prev: TimelineStep | undefined): number | null {
  if (!s.createdAt || !prev?.createdAt) return null;
  return s.createdAt - prev.createdAt;
}
